import { Link } from 'react-router-dom'
import { useState } from 'react'
import { BriefcaseBusiness } from 'lucide-react'
import { usePlatformSettings } from '@/hooks/usePlatformSettings'
import { PLATFORM_URLS } from '@/lib/env'
import AgreementDialog from '@/components/common/AgreementDialog'

type FooterLink = { label: string; path: string; external?: boolean }

export default function Footer() {
  const [agreementDialog, setAgreementDialog] = useState<{ slug: string; open: boolean }>({
    slug: '',
    open: false,
  })

  const {
    platformName,
    platformLogo,
    icpNumber,
    psbNumber,
    icpLogo,
    psbLogo,
    contactPhone,
    contactEmail,
    contactAddress,
  } = usePlatformSettings()

  const description = '惠州跨区域校企协作平台，深化校企合作，赋能人才就业。'
  const companyUrl = PLATFORM_URLS.company
  const footerLinks: Record<string, FooterLink[]> = {
    求职者: [
      { label: '找工作', path: '/jobs' },
      { label: '人才入驻', path: '/register' },
      { label: '求职指南', path: '/policy' },
      { label: '简历模板', path: '/profile' },
    ],
    企业服务: [
      { label: '发布职位', path: `${companyUrl}?view=jobs`, external: true },
      { label: '人才搜索', path: '/talent' },
      { label: '招聘会', path: '/jobfair' },
      { label: '企业入驻', path: `${companyUrl}?register=1`, external: true },
    ],
    关于平台: [
      { label: '关于我们', path: '/about' },
      { label: '政策资讯', path: '/policy' },
      { label: '联系方式', path: '/about' },
      { label: '帮助中心', path: '/about' },
    ],
  }

  return (
    <footer className="gradient-dark">
      {/* Top Section */}
      <div className="max-w-[1280px] mx-auto px-8 py-12 flex flex-wrap justify-between gap-10">
        {/* Brand Column */}
        <div className="w-[300px] space-y-3.5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-[10px] gradient-primary shadow-[0_4px_14px_rgba(54,98,236,0.5)] flex items-center justify-center overflow-hidden">
              {platformLogo ? (
                <img src={platformLogo} alt={`${platformName} logo`} className="h-full w-full bg-white object-contain p-1" />
              ) : (
                <BriefcaseBusiness className="w-[18px] h-[18px] text-white" />
              )}
            </div>
            <span className="text-white text-[17px] font-bold">{platformName}</span>
          </div>
          <p className="text-white/50 text-[13px] leading-[1.6]">
            {description}
          </p>
          {(contactPhone || contactEmail || contactAddress) && (
            <div className="space-y-1 text-[12px] leading-relaxed text-white/40">
              {contactPhone && <p>联系电话：{contactPhone}</p>}
              {contactEmail && <p>联系邮箱：{contactEmail}</p>}
              {contactAddress && <p>联系地址：{contactAddress}</p>}
            </div>
          )}
        </div>

        {/* Link Columns */}
        {Object.entries(footerLinks).map(([title, links]) => (
          <div key={title} className="space-y-3.5">
            <h4 className="text-white text-sm font-semibold">{title}</h4>
            <ul className="space-y-3.5">
              {links.map(link => (
                <li key={link.label}>
                  {link.external ? (
                    <a href={link.path} className="text-white/50 text-[13px] hover:text-white/80 transition-colors">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.path} className="text-white/50 text-[13px] hover:text-white/80 transition-colors">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom Section */}
      <div className="border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-8 py-5">
          {/* 法律协议链接 */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-3 text-white/40 text-xs">
            <button
              onClick={() => setAgreementDialog({ slug: 'user-agreement', open: true })}
              className="hover:text-white/60 transition-colors cursor-pointer"
            >
              用户协议
            </button>
            <span className="text-white/20">|</span>
            <button
              onClick={() => setAgreementDialog({ slug: 'platform-disclaimer', open: true })}
              className="hover:text-white/60 transition-colors cursor-pointer"
            >
              平台免责声明
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-white/30 text-xs">
            <span>© {new Date().getFullYear()} {platformName}</span>
            {icpNumber && (
              <span className="inline-flex items-center gap-1">
                {icpLogo && <img src={icpLogo} alt="" className="h-4 w-4 object-contain" />}
                {icpNumber}
              </span>
            )}
            {psbNumber && (
              <span className="inline-flex items-center gap-1">
                {psbLogo && <img src={psbLogo} alt="" className="h-4 w-4 object-contain" />}
                {psbNumber}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 协议弹窗 */}
      <AgreementDialog
        slug={agreementDialog.slug}
        open={agreementDialog.open}
        onOpenChange={(open) => setAgreementDialog({ slug: agreementDialog.slug, open })}
      />
    </footer>
  )
}
